import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDocumentTitle } from '@/lib/seo'
import { useLocale } from '@/i18n/useLocale'
import { localePath } from '@/lib/routes'
import { listPosts, type Post, type PostTag } from '@/lib/news'
import { NewsletterSignup } from '@/components/NewsletterSignup'

const PAGE_SIZE = 12

const FILTERS: Array<{ key: 'all' | PostTag; labelKey: string }> = [
  { key: 'all', labelKey: 'newsIndex.filterAll' },
  { key: 'release', labelKey: 'newsIndex.filterReleases' },
  { key: 'post', labelKey: 'newsIndex.filterPosts' },
  { key: 'milestone', labelKey: 'newsIndex.filterMilestones' },
]

function FeaturedCard({ post, locale }: { post: Post; locale: 'en' }) {
  return (
    <Link
      to={localePath(locale, `news/${post.slug}`)}
      className="block p-6 rounded-lg marker-dot-card-inline"
    >
      <div className="text-base uppercase tracking-wider opacity-60">{post.tag}</div>
      <h2 className="mt-2 text-3xl md:text-4xl font-bold font-display">{post.title}</h2>
      <div className="mt-2 text-base opacity-60">{post.date}</div>
      <p className="mt-3 text-lg opacity-80">{post.summary}</p>
    </Link>
  )
}

function SmallCard({ post, locale }: { post: Post; locale: 'en' }) {
  return (
    <Link
      to={localePath(locale, `news/${post.slug}`)}
      className="block p-4 rounded-lg marker-dot-card-inline"
    >
      <div className="text-base uppercase tracking-wider opacity-60">{post.tag}</div>
      <h3 className="mt-1 text-xl font-semibold">{post.title}</h3>
      <div className="mt-1 text-base opacity-60">{post.date}</div>
      <p className="mt-2 text-lg opacity-75">{post.summary}</p>
    </Link>
  )
}

export default function NewsIndex() {
  const { t } = useTranslation()
  const locale = useLocale()
  const [params, setParams] = useSearchParams()
  useDocumentTitle(`${t('nav.news')} — ${t('site.brandLong')}`)

  const tagParam = (params.get('tag') ?? 'all') as 'all' | PostTag
  const pageParam = Math.max(1, Number(params.get('page') ?? '1'))

  const filtered = useMemo(
    () => (tagParam === 'all' ? listPosts() : listPosts({ tag: tagParam })),
    [tagParam],
  )

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageStart = (pageParam - 1) * PAGE_SIZE
  const pagePosts = filtered.slice(pageStart, pageStart + PAGE_SIZE)
  const featured = pagePosts[0]
  const rest = pagePosts.slice(1)

  const setFilter = (key: 'all' | PostTag) => {
    const next = new URLSearchParams(params)
    if (key === 'all') next.delete('tag')
    else next.set('tag', key)
    next.delete('page')
    setParams(next, { replace: true })
  }

  const setPage = (n: number) => {
    const next = new URLSearchParams(params)
    next.set('page', String(n))
    setParams(next)
  }

  return (
    <section className="max-w-5xl mx-auto px-5 md:px-8 py-16">
      <header className="mb-8">
        <h1 className="text-4xl font-bold font-display">{t('nav.news')}</h1>
        <p className="mt-2 text-lg opacity-75 max-w-prose">{t('lorem:newsIndex.missionStatement')}</p>
      </header>

      <div className="md:hidden mb-6">
        <NewsletterSignup />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map((f) => {
          const isActive = tagParam === f.key
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={isActive}
              className={`pr-3 py-1.5 rounded-full text-base ${
                isActive
                  ? 'pl-3 bg-mono-800 text-mono-100 dark:bg-mono-100 dark:text-mono-900'
                  : 'marker-dot opacity-80'
              }`}
            >
              {t(f.labelKey)}
            </button>
          )
        })}
      </div>

      <div className="grid md:grid-cols-[1fr_280px] gap-8">
        <div>
          {featured && (
            <div className="mb-4">
              <FeaturedCard post={featured} locale={locale} />
            </div>
          )}
          {rest.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {rest.map((p) => (
                <SmallCard key={p.slug} post={p} locale={locale} />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <nav className="mt-8 flex items-center gap-3 text-base" aria-label="Pagination">
              <button
                type="button"
                disabled={pageParam <= 1}
                onClick={() => setPage(pageParam - 1)}
                className="pr-3 py-1.5 rounded marker-dot disabled:opacity-40"
              >
                {t('newsIndex.pagePrev')}
              </button>
              <span className="opacity-70">
                {t('newsIndex.page')} {pageParam} / {totalPages}
              </span>
              <button
                type="button"
                disabled={pageParam >= totalPages}
                onClick={() => setPage(pageParam + 1)}
                className="pr-3 py-1.5 rounded marker-dot disabled:opacity-40"
              >
                {t('newsIndex.pageNext')}
              </button>
            </nav>
          )}
        </div>

        <aside className="hidden md:block">
          <NewsletterSignup />
        </aside>
      </div>
    </section>
  )
}
